// services/periodsApi.js
//
// Assumed location: this file lives at
//   <same folder as PerformancePeriods.js>/services/periodsApi.js
// i.e. one level deeper than PerformancePeriods.js itself (same idea as
// Users/api.js sitting next to the Users components).
//
// The relative import paths below ('../../Auth/Access', '../../config')
// assume PerformancePeriods.js sits one level under `src/` (e.g.
// `src/Groups/PerformancePeriods.js`, same level as `src/Users/`), so from
// `src/Groups/services/periodsApi.js` it's two levels up to `src/`. If your
// PerformancePeriods.js lives somewhere else, adjust the `../` count to
// match — same rule as any other services file in this codebase.

import accessManager from '../../Auth/Access';
import BASE_URL from '../../config'; // eslint-disable-line no-unused-vars -- kept for parity with other services files; accessManager already knows BASE_URL

// Performance Periods dashboard API — backs PerformancePeriods.js.
// Talks to the new global (all-groups) endpoint:
//   GET /performance-periods/
// which is NOT nested under /groups/<id>/ like the other performance
// endpoints in services/api.js's `performanceApi`, because this page lists
// periods across every group at once.
export const periodsApi = {
  /**
   * Fetch performance periods across all groups, optionally filtered.
   *
   * @param {Object} filters
   * @param {string} [filters.startDate]      'YYYY-MM-DD', filters on last-meeting date (period_end)
   * @param {string} [filters.endDate]        'YYYY-MM-DD', filters on last-meeting date (period_end)
   * @param {string} [filters.status]         'open' | 'active' | 'closed'
   * @param {boolean} [filters.isSchoolFees]  true -> 'sf' periods only, false -> 'regular' only
   * @param {string|number} [filters.groupId] restrict to one group
   * @param {string} [filters.search]         group name search (server-side; the component also
   *                                          does its own client-side search, so this is optional)
   * @returns {Promise<Array>} periods shaped as
   *   { id, type: 'sf'|'regular', group: {id, name}, officerName, lastMeetingDate, status }
   */
  fetchPerformancePeriods: async (filters = {}) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const params = new URLSearchParams();

    if (filters.startDate) params.append('start_date', filters.startDate);
    if (filters.endDate) params.append('end_date', filters.endDate);
    if (filters.status && filters.status !== 'all') params.append('status', filters.status);
    if (filters.isSchoolFees !== undefined) {
      params.append('is_school_fees', filters.isSchoolFees ? 'true' : 'false');
    }
    if (filters.groupId) params.append('group_id', filters.groupId);
    if (filters.search && filters.search.trim()) params.append('search', filters.search.trim());

    const endpoint = `/performance-periods/${params.toString() ? '?' + params.toString() : ''}`;

    console.log('API Request URL:', endpoint);

    try {
      const response = await accessManager.request(endpoint, { method: 'GET' });

      // Backend returns a plain array (no pagination wrapper) — but guard
      // against either shape, same defensiveness as groupsApi.getGroups.
      if (Array.isArray(response)) return response;
      if (response && Array.isArray(response.results)) return response.results;
      return [];
    } catch (error) {
      console.error('❌ [API] Fetch Performance Periods Error:', error);
      throw error;
    }
  },
};

export default periodsApi;